/*
 * Copyright (C) 2019 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { CuttlefishConnection } from "../types";
import { getAdbMessageEmitter } from "./adbMessageEmitter";

/**
 * ADB Protocol Implementation for Cuttlefish WebRTC
 *
 * Implements the Android Debug Bridge (ADB) protocol over WebRTC data channels.
 * This allows shell command execution and other ADB operations on the virtual device.
 */

const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder();

// ADB protocol message types
const A_CNXN = 0x4e584e43; // CNXN
const A_OPEN = 0x4e45504f; // OPEN
const A_WRTE = 0x45545257; // WRTE
const A_OKAY = 0x59414b4f; // OKAY
const A_CLSE = 0x45534c43; // CLSE

let nextLocalChannelId = 1;
const timeoutMs = 10000;
const CNXN_MAX_RETRIES = 3;
const CNXN_RETRY_DELAY_MS = 2000;

/**
 * ADB WebSocket wrapper interface
 * Provides send method compatible with the ADB implementation
 */
interface AdbWebSocket {
  send: (buffer: ArrayBuffer) => void;
}

/**
 * ADB Connection Class
 * Manages ADB protocol communication with a Cuttlefish device
 */
export class AdbConnection {
  private adbWs?: AdbWebSocket;
  private array: Uint8Array = new Uint8Array(0);
  private connectedCb?: () => void;
  private disconnectedCb?: () => void;
  private disconnectedTimeout?: ReturnType<typeof setTimeout>;
  private initialConnectionEstablished: boolean = false;
  private localChannelId: number;

  constructor() {
    this.localChannelId = nextLocalChannelId++;
  }

  /**
   * Check if the ADB connection is established
   */
  get isConnected(): boolean {
    return this.initialConnectionEstablished && this.adbWs !== undefined;
  }

  /**
   * Set 32-bit unsigned integer in little-endian format
   */
  private setU32LE(array: Uint8Array, offset: number, x: number): void {
    array[offset] = x & 0xff;
    array[offset + 1] = (x >> 8) & 0xff;
    array[offset + 2] = (x >> 16) & 0xff;
    array[offset + 3] = x >> 24;
  }

  /**
   * Get 32-bit unsigned integer from little-endian format
   */
  private getU32LE(array: Uint8Array, offset: number): number {
    const x =
      (array[offset] ?? 0) |
      ((array[offset + 1] ?? 0) << 8) |
      ((array[offset + 2] ?? 0) << 16) |
      ((array[offset + 3] ?? 0) << 24);

    return x >>> 0; // convert signed to unsigned if necessary
  }

  /**
   * Compute checksum for ADB message payload
   */
  private computeChecksum(array: Uint8Array): number {
    let sum = 0;
    for (let i = 0; i < array.length; ++i) {
      sum = ((sum + (array[i] ?? 0)) & 0xffffffff) >>> 0;
    }
    return sum;
  }

  /**
   * Create an ADB protocol message
   */
  private createAdbMessage(
    command: number,
    arg0: number,
    arg1: number,
    payload: Uint8Array,
  ): ArrayBuffer {
    const arrayBuffer = new ArrayBuffer(24 + payload.length);
    const array = new Uint8Array(arrayBuffer);

    if (array) {
      this.setU32LE(array, 0, command);
      this.setU32LE(array, 4, arg0);
      this.setU32LE(array, 8, arg1);
      this.setU32LE(array, 12, payload.length);
      this.setU32LE(array, 16, this.computeChecksum(payload));
      this.setU32LE(array, 20, command ^ 0xffffffff);
      array.set(payload, 24);
    }

    return arrayBuffer;
  }

  /**
   * Join two Uint8Arrays
   */
  private joinArrays(arr1: Uint8Array, arr2: Uint8Array): Uint8Array {
    const arr = new Uint8Array(arr1.length + arr2.length);
    arr.set(arr1, 0);
    arr.set(arr2, arr1.length);
    return arr;
  }

  /**
   * Start connection timeout timer
   */
  private awaitConnection(): void {
    clearTimeout(this.disconnectedTimeout);
    if (this.disconnectedCb) {
      this.disconnectedTimeout = setTimeout(this.disconnectedCb, timeoutMs);
    }
  }

  /**
   * Mark connection as connected (stops timeout timer)
   */
  private connected(): void {
    if (this.disconnectedTimeout) {
      clearTimeout(this.disconnectedTimeout);
    }
    if (!this.initialConnectionEstablished) {
      this.initialConnectionEstablished = true;
      if (this.connectedCb) {
        this.connectedCb();
      }
    }
  }

  /**
   * Open ADB connection with handshake
   */
  private adbOpenConnection(retryCount = 0): void {
    if (!this.adbWs) {
      console.error("ADB WebSocket not initialized");
      return;
    }

    const systemIdentity = utf8Encoder.encode("Cray_II:1234:whatever");
    const arrayBuffer = this.createAdbMessage(
      A_CNXN,
      0x1000000,
      256 * 1024,
      systemIdentity,
    );
    this.adbWs.send(arrayBuffer);

    if (retryCount < CNXN_MAX_RETRIES) {
      const retryTimer = setTimeout(() => {
        if (!this.initialConnectionEstablished && this.adbWs) {
          console.warn(`[ADB] CNXN retry ${retryCount + 1}/${CNXN_MAX_RETRIES}`);
          this.adbOpenConnection(retryCount + 1);
        }
      }, CNXN_RETRY_DELAY_MS);

      if (!this.initialConnectionEstablished) {
        setTimeout(() => {
          if (this.initialConnectionEstablished) {
            clearTimeout(retryTimer);
          }
        }, CNXN_RETRY_DELAY_MS - 100);
      }
    }
  }

  /**
   * Send OKAY acknowledgment message
   */
  private adbSendOkay(localId: number, remoteId: number): void {
    if (!this.adbWs) {
      console.error("ADB WebSocket not initialized");
      return;
    }

    const payload = new Uint8Array(0);
    const arrayBuffer = this.createAdbMessage(
      A_OKAY,
      localId,
      remoteId,
      payload,
    );
    this.adbWs.send(arrayBuffer);
  }

  /**
   * Handle incoming ADB message
   */
  private adbOnMessage(arrayBuffer: ArrayBuffer): void {
    this.array = this.joinArrays(this.array, new Uint8Array(arrayBuffer));

    while (this.array.length > 0) {
      if (this.array.length < 24) {
        // Incomplete package, must wait for more data
        return;
      }

      const command = this.getU32LE(this.array, 0);
      const magic = this.getU32LE(this.array, 20);

      if (command !== (magic ^ 0xffffffff) >>> 0) {
        console.error("adb message command vs magic failed.");
        console.error("command = " + command + ", magic = " + magic);
        return;
      }

      const payloadLength = this.getU32LE(this.array, 12);

      if (this.array.length < 24 + payloadLength) {
        // Incomplete package, must wait for more data
        return;
      }

      const payloadChecksum = this.getU32LE(this.array, 16);
      const checksum = this.computeChecksum(
        this.array.slice(24, 24 + payloadLength),
      );

      if (payloadChecksum !== checksum) {
        console.error("adb message checksum mismatch.");
        // This can happen if a shell command executes while another
        // channel is receiving data.
      }

      switch (command) {
        case A_CNXN: {
          console.info("WebRTC adb connected.");
          this.connected();
          break;
        }

        case A_OKAY: {
          const remoteId = this.getU32LE(this.array, 4);
          console.debug("WebRTC adb channel created w/ remoteId " + remoteId);
          this.connected();
          break;
        }

        case A_WRTE: {
          const remoteId = this.getU32LE(this.array, 4);
          const localId = this.getU32LE(this.array, 8);
          this.adbSendOkay(localId, remoteId);

          const adbEmitter = getAdbMessageEmitter();
          adbEmitter.emit(this.array.slice(0, 24 + payloadLength).buffer);
          break;
        }

        case A_CLSE: {
          const remoteId = this.getU32LE(this.array, 4);
          const localId = this.getU32LE(this.array, 8);
          const clseMsg = this.createAdbMessage(
            A_CLSE,
            localId,
            remoteId,
            new Uint8Array(0),
          );
          this.adbWs?.send(clseMsg);

          const adbEmitter = getAdbMessageEmitter();
          adbEmitter.emit(this.array.slice(0, 24 + payloadLength).buffer);
          break;
        }
      }

      this.array = new Uint8Array(
        this.array.subarray(24 + payloadLength, this.array.length),
      );
    }
  }

  /**
   * Execute shell command via ADB
   */
  adbShell(command: string): number {
    if (!this.adbWs) {
      console.error("ADB WebSocket not initialized");
      return -1;
    }

    const channelId = nextLocalChannelId++;
    const destination = utf8Encoder.encode("shell:" + command);
    const arrayBuffer = this.createAdbMessage(
      A_OPEN,
      channelId,
      0,
      destination,
    );

    this.adbWs.send(arrayBuffer);
    // Only await connection for initial handshake, not for each shell command
    // Shell commands open new channels, not new connections
    if (!this.initialConnectionEstablished) {
      this.awaitConnection();
    }

    return channelId;
  }

  /**
   * Initialize ADB connection
   * @param devConn - Cuttlefish device connection
   * @param connectedCb - Callback when connection established
   * @param disconnectedCb - Callback when connection lost/timeout
   */
  init(
    devConn: CuttlefishConnection,
    connectedCb?: () => void,
    disconnectedCb?: () => void,
  ): void {
    if (this.adbWs) {
      console.warn("ADB already initialized");
      return;
    }

    this.adbWs = {
      send: (buffer: ArrayBuffer) => {
        if (devConn.sendAdbMessage) {
          devConn.sendAdbMessage(buffer);
        } else {
          console.error("sendAdbMessage not available on device connection");
        }
      },
    };

    this.connectedCb = connectedCb;
    this.disconnectedCb = disconnectedCb;
    this.awaitConnection();

    // Set up ADB message handling: raw data channel → adb protocol parser → emitter
    // adb.adbOnMessage handles accumulation/fragmentation and emits parsed WRTE/CLSE
    // to the emitter for output collectors and UI subscribers.
    if (devConn.onAdbMessage) {
      devConn.onAdbMessage((msg) => this.adbOnMessage(msg));

      console.debug(
        "ADB message handler attached (emitter subscribers:",
        getAdbMessageEmitter().handlerCount,
        ")",
      );
    } else {
      console.error("onAdbMessage not available on device connection");
    }

    console.debug("ADB initialized, opening connection...");
    this.adbOpenConnection();
  }

  /**
   * Close ADB connection and cleanup
   */
  close(): void {
    if (this.disconnectedTimeout) {
      clearTimeout(this.disconnectedTimeout);
    }
    this.adbWs = undefined;
    this.array = new Uint8Array();
    this.connectedCb = undefined;
    this.disconnectedCb = undefined;
    this.initialConnectionEstablished = false;
  }
}

/**
 * Create and initialize an ADB connection
 * @param devConn - Cuttlefish device connection
 * @param onConnected - Callback when connection established
 * @param onDisconnected - Callback when connection lost/timeout
 * @returns AdbConnection instance
 */
export function createAdbConnection(
  devConn: CuttlefishConnection,
  onConnected?: () => void,
  onDisconnected?: () => void,
): AdbConnection {
  const adb = new AdbConnection();
  adb.init(devConn, onConnected, onDisconnected);
  return adb;
}
